import type { CSSProperties } from 'react'
import { isNumber } from '../assertion'

export const dataAttr = (condition: boolean | undefined) =>
  (condition ? '' : undefined) as boolean | 'true' | 'false'

export const ariaAttr = (condition: boolean | undefined) =>
  condition ? true : undefined

export function canUseDOM(): boolean {
  return !!(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
  )
}

export const isBrowser = canUseDOM()

export function isElement(el: any): el is Element {
  return (
    el != null &&
    typeof el == 'object' &&
    'nodeType' in el &&
    el.nodeType === Node.ELEMENT_NODE
  )
}

export function isHTMLElement(el: any): el is HTMLElement {
  if (!isElement(el)) {
    return false
  }

  const win = el.ownerDocument.defaultView ?? window
  return el instanceof win.HTMLElement
}

export function getOwnerWindow(node?: Element | null): typeof globalThis {
  return isElement(node)
    ? getOwnerDocument(node)?.defaultView ?? window
    : window
}

export function getOwnerDocument(node?: Element | null): Document {
  return isElement(node) ? node.ownerDocument ?? document : document
}

export function getEventWindow(event: Event): typeof globalThis {
  return ((event as UIEvent).view ?? window) as unknown as typeof globalThis
}

export function getActiveElement(node?: HTMLElement | null) {
  const doc = getOwnerDocument(node)
  return doc?.activeElement as HTMLElement
}

export function getRelatedTarget(
  event: Pick<FocusEvent, 'relatedTarget' | 'target' | 'currentTarget'>
) {
  const target = (event.target ?? event.currentTarget) as HTMLElement
  const activeElement = getActiveElement(target)
  return (event.relatedTarget ?? activeElement) as HTMLElement
}

export function contains(parent: HTMLElement | null, child: HTMLElement) {
  if (!parent) return false
  return parent === child || parent.contains(child)
}

export function addDomEvent(
  target: EventTarget,
  eventName: string,
  handler: EventListener,
  options?: AddEventListenerOptions
) {
  target.addEventListener(eventName, handler, options)
  return () => {
    target.removeEventListener(eventName, handler, options)
  }
}

export function mergeClassnames(...classNames: any[]) {
  return classNames.filter(Boolean).join(' ') || undefined
}

export function getStringWidth(
  value: string = '',
  styles: CSSProperties = {}
): number {
  const {
    fontSize = 15,
    lineHeight = null,
    letterSpacing = null,
    padding = 0,
    ...rest
  } = styles
  const div = document.createElement('div')
  div.innerText = value

  const _fontSize = isNumber(fontSize) ? `${fontSize}px` : fontSize
  const _lineHeight = isNumber(lineHeight) ? `${lineHeight}px` : lineHeight
  const _letterSpacing = isNumber(letterSpacing)
    ? `${letterSpacing}px`
    : letterSpacing
  const _padding = isNumber(padding) ? `0 ${padding}px` : `0 ${padding}`

  Object.assign(div.style, {
    fontSize: _fontSize,
    lineHeight: _lineHeight,
    letterSpacing: _letterSpacing,
    padding: _padding,
    width: 'auto',
    display: 'block',
    visibility: 'hidden',
    position: 'fixed',
    overflow: 'auto',
    boxSizing: 'border-box',
    ...rest
  })

  document.body.append(div)
  const width = div.clientWidth
  div.remove()

  return width
}
